import { supabase } from '../lib/supabase';
import { contractValidation } from '../utils/validationUtils';

export const marketingContractService = {
  // Calculate retainer amounts based on percentages
  calculateRetainerAmounts(contractAmount, arddjoPercentage, ownerPercentage) {
    const arddjoAmount = (contractAmount * arddjoPercentage) / 100;
    const ownerAmount = (contractAmount * ownerPercentage) / 100;
    return {
      arddjoRetainerAmount: arddjoAmount,
      ownerRetainerAmount: ownerAmount,
      totalRetainerAmount: arddjoAmount + ownerAmount
    };
  },

  // Create marketing contract
  async createMarketingContract(contractData) {
    try {
      // SERVER-SIDE VALIDATION
      const validation = contractValidation?.validateMarketingContract(contractData);
      if (!validation?.isValid) {
        const errorMessages = Object.values(validation?.errors)?.join('، ');
        throw new Error(`خطأ في البيانات: ${errorMessages}`);
      }

      const { data, error } = await supabase?.from('marketing_contracts')?.insert({
        contract_id: contractData?.contractId,
        arddjo_retainer_percentage: contractData?.arddjoRetainerPercentage,
        owner_retainer_percentage: contractData?.ownerRetainerPercentage,
        marketing_duration_months: contractData?.marketingDurationMonths || 6,
        exclusive_rights: contractData?.exclusiveRights ?? true,
        marketing_channels: contractData?.marketingChannels || [],
        target_price: contractData?.targetPrice,
        minimum_price: contractData?.minimumPrice,
        notes: contractData?.notes
      })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating marketing contract:', error);
      throw error;
    }
  },

  // Get marketing contract by contract ID
  async getMarketingContract(contractId) {
    try {
      const { data, error } = await supabase?.from('marketing_contracts')?.select(`
          *,
          contract:contracts(
            contract_number,
            contract_type,
            amount,
            status
          ),
          arddjo_accepted_by_profile:user_profiles!marketing_contracts_arddjo_accepted_by_fkey(
            full_name,
            email
          ),
          owner_accepted_by_profile:user_profiles!marketing_contracts_owner_accepted_by_fkey(
            full_name,
            email
          )
        `)?.eq('contract_id', contractId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching marketing contract:', error);
      throw error;
    }
  },

  // Update marketing contract
  async updateMarketingContract(marketingContractId, updates) {
    try {
      const { data, error } = await supabase?.from('marketing_contracts')?.update({
        arddjo_retainer_percentage: updates?.arddjoRetainerPercentage,
        owner_retainer_percentage: updates?.ownerRetainerPercentage,
        marketing_duration_months: updates?.marketingDurationMonths,
        exclusive_rights: updates?.exclusiveRights,
        marketing_channels: updates?.marketingChannels,
        target_price: updates?.targetPrice,
        minimum_price: updates?.minimumPrice,
        notes: updates?.notes,
        updated_at: new Date()?.toISOString()
      })?.eq('id', marketingContractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating marketing contract:', error);
      throw error;
    }
  },

  // ARDDJO acceptance
  async arddjoAcceptance(marketingContractId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('marketing_contracts')?.update({
        arddjo_acceptance_status: status,
        arddjo_accepted_by: status === 'accepted' ? user?.id : null,
        arddjo_accepted_at: status === 'accepted' ? new Date()?.toISOString() : null
      })?.eq('id', marketingContractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating ARDDJO acceptance:', error);
      throw error;
    }
  },

  // Owner acceptance
  async ownerAcceptance(marketingContractId, status) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('marketing_contracts')?.update({
        owner_acceptance_status: status,
        owner_accepted_by: status === 'accepted' ? user?.id : null,
        owner_accepted_at: status === 'accepted' ? new Date()?.toISOString() : null
      })?.eq('id', marketingContractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating owner acceptance:', error);
      throw error;
    }
  },

  // Get all marketing contracts with filters
  async getMarketingContracts(filters = {}) {
    try {
      let query = supabase?.from('marketing_contracts')?.select(`
          *,
          contract:contracts(
            contract_number,
            contract_type,
            amount,
            status,
            property:properties(
              title,
              location
            )
          )
        `);

      if (filters?.arddjoAcceptanceStatus) {
        query = query?.eq('arddjo_acceptance_status', filters?.arddjoAcceptanceStatus);
      }

      if (filters?.ownerAcceptanceStatus) {
        query = query?.eq('owner_acceptance_status', filters?.ownerAcceptanceStatus);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching marketing contracts:', error);
      throw error;
    }
  },

  // Real-time subscription for marketing contract updates
  subscribeToMarketingContract(contractId, callback) {
    const channel = supabase?.channel(`marketing-contract-${contractId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'marketing_contracts',
          filter: `contract_id=eq.${contractId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};